import { goStorageAgent } from 'src/api/goStorageAgent';
import { throwApiError } from 'src/api/utils';
import type { IAuthService } from 'src/app/auth/AuthStore';

export type GoStorageObject<T> = {
  createdAt?: number;
  data: T;
  id: number;
  objectType?: string;
  status: 'ACTIVE';
};
export type LocationNameDisplayType = 'name' | 'abbreviatedName' | 'prefix';

export type UserSelectedDepthIndex = 'INDEX_DEPTH_SCALE_METERS' | 'INDEX_DEPTH_SCALE_FEET';

export type TAzimuthReference = 'TRUE' | 'GRID' | 'MAGNETIC';

export type TUomSettings = {
  depthUomId?: number | null;
  doglegSeverityUomId?: number | null;
  coordinatesUomId?: number | null;
  toolLengthUomId?: number | null;
  diameterUomId?: number | null;
  torqueUomId?: number | null;
  densityUomId?: number | null;
};

export interface LocationNameConfig {
  order: number | null;
  displayName: LocationNameDisplayType;
}

export interface LocationNameConfigurator {
  block: LocationNameConfig;
  field: LocationNameConfig;
  pad: LocationNameConfig;
}

export type TUserPreference = {
  ownerUserId: string | null;
  autoSelectWellboreForTime: 'active' | 'main';
  autoSelectWellboreForDepth: 'active' | 'main';
  defaultWellListFilter?: 'active' | 'favorites' | 'all';
  defaultWellListGroup?:
    | 'wellsGroupingStatus'
    | 'wellsGroupingDrillingRig'
    | 'wellsGroupingRigCrew'
    | 'wellsGroupingLocation';
  locationNameConfigurator: LocationNameConfigurator;
  isAltNames: boolean;
  isColoredParamsHeaders: boolean;
  defaultDepthUnits: UserSelectedDepthIndex;
  defaultSplitRange: {
    time: number;
    depth: number;
  };
  /** Per-user favorite reference-well group template IDs (public + personal). */
  favoriteReferenceGroupIds?: string[];
  /** System_VerticalCrs id used as system datum for trajectories. */
  systemDatumId?: number | null;
  /** Azimuth reference for trajectories. */
  azimuthReference?: TAzimuthReference | null;
  /** Default units of measurement for wells and widgets. */
  uom?: TUomSettings | null;
};

export type TUserPreferenceWithGSProps = GoStorageObject<TUserPreference>;

export class UserPreferenceApi {
  private readonly auth: IAuthService;

  constructor(auth: IAuthService) {
    this.auth = auth;
  }

  async loadPreferences(): Promise<TUserPreferenceWithGSProps[]> {
    try {
      const { data } = await goStorageAgent.get<TUserPreferenceWithGSProps[]>(
        'object/list/GOdashboard_UserPreference',
        {
          params: {
            ownerUserId: this.auth.userInfo.sub,
          },
        },
      );

      return data;
    } catch (error) {
      throwApiError(error);
    }
  }

  async updatePreferences(id: number, template: TUserPreference, abortController?: AbortController): Promise<void> {
    try {
      await goStorageAgent.patch(`object/GOdashboard_UserPreference/${id}`, template, {
        signal: abortController?.signal,
      });
    } catch (error) {
      throwApiError(error);
    }
  }

  async createPreferences(template: TUserPreference): Promise<TUserPreferenceWithGSProps> {
    try {
      const { data } = await goStorageAgent.post('object/new/GOdashboard_UserPreference', template);

      return data;
    } catch (error) {
      throwApiError(error);
    }
  }
}
